/**
 * Een dakpanset maken of bewerken (stap 22), in het tabblad Teksten, de knop
 * Nieuwe dakpanset: van een standaardtekst (informatief, betogend of
 * instructief, van minstens 450 woorden: dat kijkt de server na) vier deelteksten voor een groepje van vier, met twee
 * bouwstenen per deeltekst ([1A] ... [4B]), overlap tussen twee deelteksten,
 * en een modelreconstructie in zes zinnen (services/dakpan.py).
 *
 * "Maak de dakpanset" laat het model de set maken; er wordt dan nog niets
 * bewaard. Daaronder de titel, de vier deelteksten (te bewerken, met de
 * markeringen erin), wat de app nakeek, en de opzet (components/
 * DakpanOpzet.js). Opslaan kan pas als elke deeltekst zijn twee markeringen
 * heeft. De set wordt een eigen tekst; de brontekst blijft zoals hij is.
 */
import React, { useState } from 'react';
import { Check, RefreshCw, Sparkles } from 'lucide-react';
import { beheerApi as api } from '../../../api/beheer';
import { DakpanOpzet } from '../../../components/DakpanOpzet';
import { heeftDeelteksten } from '../../../keuzelijsten';
import { markeringProbleem, woordenIn } from './VerhaalFormulier';

const WOORDEN_PER_DEEL = [150, 200];   // zoals WOORDEN_PER_DEEL_3F in services/dakpan.py

export function DakpanFormulier({ bestaand, teksten = [], onOpslaan, onAnnuleer, meldFout }) {
  // geen verhaal en geen fictionele tekst: de prompts en de toets zijn voor een zakelijke tekst (dakpan.kan_bron_zijn)
  const bronnen = teksten.filter(t => !heeftDeelteksten(t) && t.text_type !== 'fictioneel');
  const [bronId, setBronId] = useState(bestaand?.opzet?.bron?.id || '');
  const bronWeg = bronId && !bronnen.some(t => t.id === bronId);   // de brontekst is verwijderd
  const [set, setSet] = useState(bestaand ? {
    titel: bestaand.titel, beschrijving: bestaand.beschrijving || '', opzet: bestaand.opzet,
    text_type: bestaand.text_type, deelteksten: bestaand.paragraphs.map(p => p.gemarkeerd || p.inhoud), controle: null,
  } : null);
  const [maken, setMaken] = useState(false);
  const [melding, setMelding] = useState('');
  const [opslaan, setOpslaan] = useState(false);

  const maak = async () => {
    if (set && !window.confirm('Een nieuwe set vervangt de set hieronder (pas bij opslaan in de database). Doorgaan?')) return;
    setMaken(true);
    setMelding('');
    meldFout('');
    try {
      const uit = await api.schrijfDakpanset(bronId);
      if (!uit.success) {
        meldFout(uit.message);
        return;
      }
      setSet({ titel: uit.titel, beschrijving: uit.beschrijving, opzet: uit.opzet, text_type: uit.text_type,
        deelteksten: uit.deelteksten, controle: uit.controle });
      setMelding(uit.message);
    } catch (err) {
      meldFout(err.name === 'AbortError' ? 'Het maken duurde te lang. Probeer het opnieuw.' : err.message);
    } finally {
      setMaken(false);
    }
  };

  const zetDeel = (i, tekst) => setSet(s => ({ ...s, deelteksten: s.deelteksten.map((d, j) => (j === i ? tekst : d)) }));
  const problemen = set ? set.deelteksten.map((d, i) => markeringProbleem(d, i + 1)) : [];
  const kanOpslaan = set && set.titel.trim() && set.deelteksten.length === 4 && problemen.every(p => !p) && !opslaan;

  const bewaar = async (e) => {
    e.preventDefault();
    setOpslaan(true);
    try {
      await onOpslaan({
        titel: set.titel.trim(), beschrijving: set.beschrijving.trim(), text_type: set.text_type || 'informatief',
        genre: 'dakpan', opzet: set.opzet, paragraphs: set.deelteksten,
      });
    } finally {
      setOpslaan(false);
    }
  };

  return (
    <form onSubmit={bewaar} className="card" style={{ background: bestaand ? 'var(--blauw-vlak)' : 'var(--vlak)' }}>
      <h3 className="mb-16">{bestaand ? `Dakpanset bewerken — ${bestaand.titel}` : 'Nieuwe dakpanset'}</h3>
      <p className="text-small mb-16">
        Vier deelteksten voor een groepje van vier: elk kind leest er één, op zijn eigen niveau, en samen maken ze er een
        verhaal van in zes zinnen. Elke deeltekst heeft twee bouwstenen, en het einde van de ene overlapt met het begin
        van de volgende. Het model gebruikt alleen wat in de brontekst staat; die moet daarom minstens 450 woorden
        hebben. Daarna maak je de set op elk niveau in het tabblad Niveauteksten.
      </p>
      <div className="form-group">
        <label htmlFor="dp-bron">Van de standaardtekst</label>
        <select id="dp-bron" className="input" value={bronId} onChange={e => setBronId(e.target.value)}>
          <option value="">Kies een tekst</option>
          {bronWeg && <option value={bronId}>{bestaand?.opzet?.bron?.titel || 'De brontekst'} (bestaat niet meer)</option>}
          {bronnen.map(t => <option key={t.id} value={t.id}>{t.titel}</option>)}
        </select>
      </div>
      <div style={{ textAlign: 'center', margin: '8px 0 16px' }}>
        <button type="button" className="btn btn-primary" onClick={maak} disabled={!bronId || bronWeg || maken} style={{ padding: '12px 32px' }}>
          {maken ? <><RefreshCw size={16} className="spinning" /> De set wordt gemaakt...</>
            : <><Sparkles size={16} /> {set ? 'Maak de set opnieuw' : 'Maak de dakpanset'}</>}
        </button>
        {maken && <p className="text-muted text-small mt-8">Dit kan een paar minuten duren...</p>}
      </div>
      {melding && <div className="alert alert-success mb-16">{melding}</div>}

      {set && (
        <>
          <div className="grid-2">
            <div className="form-group">
              <label htmlFor="dp-titel">Titel</label>
              <input id="dp-titel" className="input" value={set.titel} onChange={e => setSet({ ...set, titel: e.target.value })} />
            </div>
            <div className="form-group">
              <label htmlFor="dp-beschrijving">Korte beschrijving</label>
              <input id="dp-beschrijving" className="input" value={set.beschrijving}
                onChange={e => setSet({ ...set, beschrijving: e.target.value })} />
            </div>
          </div>
          {set.controle?.waarschuwingen?.length > 0 && (
            <div className="alert mb-16" style={{ background: 'var(--waarschuw-vlak)', color: 'var(--waarschuw-tekst)' }}
              data-testid="dakpan-kijk-na">
              <strong>Kijk na:</strong>
              <ul style={{ margin: '4px 0 0 20px' }}>{set.controle.waarschuwingen.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
          <p className="text-muted text-small mb-8">
            De markeringen [1A] tot en met [4B] staan vóór de zinnen met de bouwstenen. De leerling ziet ze niet. Laat ze
            staan als je een deeltekst bewerkt.
          </p>
          {set.deelteksten.map((deel, i) => {
            const aantal = woordenIn(deel);
            const goed = aantal >= WOORDEN_PER_DEEL[0] && aantal <= WOORDEN_PER_DEEL[1];
            const titel = set.opzet?.delen?.[i]?.titel;
            return (
              <div className="form-group" key={i}>
                <div className="flex-between">
                  <label htmlFor={`dp-deel-${i + 1}`}>Deeltekst {i + 1}{titel ? ` — ${titel}` : ''}</label>
                  <span style={{ fontSize: 13 }}>
                    <span style={{ color: goed ? 'var(--goed-2)' : 'var(--waarschuw-tekst)' }}>{aantal} woorden</span>
                    {problemen[i] && <span style={{ color: 'var(--fout)', marginLeft: 8 }}>{problemen[i]}</span>}
                  </span>
                </div>
                <textarea id={`dp-deel-${i + 1}`} className="input" rows={6} value={deel} spellCheck={false}
                  onChange={e => zetDeel(i, e.target.value)} style={{ fontFamily: 'inherit', lineHeight: 1.6, resize: 'vertical' }} />
              </div>
            );
          })}
          <div className="card mb-16" style={{ background: 'var(--wit)' }}>
            <DakpanOpzet opzet={set.opzet} />
          </div>
        </>
      )}
      <div className="flex gap-8">
        <button type="submit" className="btn btn-success" disabled={!kanOpslaan}>
          <Check size={16} /> Opslaan en sluiten
        </button>
        <button type="button" className="btn btn-secondary" onClick={onAnnuleer}>Annuleren</button>
      </div>
    </form>
  );
}
